import dayjs from 'dayjs';

/**
 * Calculate the duration between two dates in a human-readable format
 * @param {Date|string} startDate - The start date
 * @param {Date|string|null} endDate - The end date (null for ongoing)
 * @returns {string} - Formatted duration like "2 years", "1 year 3 months", "6 months", "Ongoing"
 */
export const calculateBondDuration = (startDate, endDate) => {
  if (!startDate) return '--';

  const start = dayjs(startDate);
  const end = endDate ? dayjs(endDate) : dayjs();

  // Calculate total months difference
  const monthsDiff = end.diff(start, 'month');

  if (monthsDiff < 0) {
    return '0 months';
  }

  if (monthsDiff === 0) {
    const daysDiff = end.diff(start, 'day');
    return `${daysDiff} day${daysDiff !== 1 ? 's' : ''}`;
  }

  const years = Math.floor(monthsDiff / 12);
  const remainingMonths = monthsDiff % 12;

  const parts = [];
  if (years > 0) {
    parts.push(`${years} year${years !== 1 ? 's' : ''}`);
  }
  if (remainingMonths > 0) {
    parts.push(`${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`);
  }

  return parts.join(' ');
};

/**
 * Get the overall bond status for an employee based on all their bonds
 * @param {Object} employee - Employee object with employee_bonds array
 * @returns {string|null} - 'ACTIVE', 'EXPIRED', or latest bond status, or null if no bonds
 */
export const getOverallBondStatus = (employee) => {
  const bonds = employee?.employee_bonds || [];

  if (bonds.length === 0) {
    return null;
  }

  // If any bond is ACTIVE, overall status is ACTIVE
  const hasActive = bonds.some((bond) => bond.status === 'ACTIVE');
  if (hasActive) {
    return 'ACTIVE';
  }

  // If all bonds are EXPIRED, overall status is EXPIRED
  const allExpired = bonds.every((bond) => bond.status === 'EXPIRED');
  if (allExpired) {
    return 'EXPIRED';
  }

  // Otherwise, return the status of the most recent bond (by start_date)
  const sorted = [...bonds].sort((a, b) => new Date(b.start_date) - new Date(a.start_date));

  return sorted[0]?.status || 'PENDING';
};
